import { useAuth0 } from "@auth0/auth0-react"
import { useQuery } from "@tanstack/react-query"

import { apiClient } from "../../lib/apiClient.js"
import { IS_NATIVE_APP } from "../../config/appTarget.js"
import {
  ALL_FEATURES_OFF,
  type FeatureFlagKey,
  type FeatureFlagMap,
} from "../../../shared/lib/featureFlags.js"
import { normalizeFeatureFlags } from "./normalize.js"

export const FEATURE_FLAGS_QUERY_KEY = ["feature-flags"] as const

export function useFeatureFlags(): { flags: FeatureFlagMap; isLoading: boolean } {
  const { isAuthenticated, isLoading: isAuthLoading, user, getAccessTokenSilently } = useAuth0()

  const query = useQuery({
    queryKey: [...FEATURE_FLAGS_QUERY_KEY, isAuthenticated ? (user?.sub ?? "auth") : "anon"],
    enabled: !isAuthLoading,
    // Load once per page load; a browser refresh picks up flag changes.
    staleTime: 10 * 60_000,
    refetchOnMount: false,
    queryFn: async () => {
      const headers: Record<string, string> = {}
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: import.meta.env.VITE_AUTH0_AUDIENCE,
              scope: "openid profile email",
            },
            timeoutInSeconds: 15,
          })
          headers.Authorization = `Bearer ${token}`
        } catch {
          // Fall back to the anonymous evaluation.
        }
      }
      const raw = await apiClient.get("/api/feature-flags", { headers })
      return normalizeFeatureFlags(raw)
    },
  })

  const flags = query.data ?? ALL_FEATURES_OFF
  // Paid tiers need IAP on native (App Store 3.1.1 / Play Billing), so never show them there.
  const effective = IS_NATIVE_APP && flags.paid_tiers ? { ...flags, paid_tiers: false } : flags

  // isPending (not isLoading) so the disabled-while-auth-loads phase also counts as loading.
  return { flags: effective, isLoading: query.isPending }
}

export function useFeatureFlag(key: FeatureFlagKey): boolean {
  return useFeatureFlags().flags[key]
}
